import React, { useEffect, useRef, useState } from 'react';
import { Maximize2Icon, Minimize2Icon, MinusIcon, MoveIcon, PlusIcon, RotateCcwIcon } from 'lucide-react';
interface BeachCameraViewerProps {
  isReady: boolean;
}
interface Position {
  x: number;
  y: number;
}
interface DragStart extends Position {
  pointerX: number;
  pointerY: number;
}
interface PinchStart {
  distance: number;
  zoom: number;
}
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;
const ZOOM_STEP = 0.25;
const VIDEO_ID = 'h9IcLCNXwLo';
export function BeachCameraViewer({
  isReady
}: BeachCameraViewerProps) {
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [position, setPosition] = useState<Position>({
    x: 0,
    y: 0
  });
  const [isInteractionActive, setIsInteractionActive] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const viewportRef = useRef<HTMLDivElement>(null);
  const pointersRef = useRef<Map<number, Position>>(new Map());
  const dragStartRef = useRef<DragStart | null>(null);
  const pinchStartRef = useRef<PinchStart | null>(null);
  useEffect(() => {
    if (!isExpanded) return;
    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded]);
  const getBounds = (nextZoom = zoom) => {
    const rect = viewportRef.current?.getBoundingClientRect();
    if (!rect) return {
      maxX: 0,
      maxY: 0
    };
    return {
      maxX: rect.width * (nextZoom - 1) / 2,
      maxY: rect.height * (nextZoom - 1) / 2
    };
  };
  const setClampedZoom = (nextZoom: number) => {
    const clampedZoom = clamp(nextZoom, MIN_ZOOM, MAX_ZOOM);
    const {
      maxX,
      maxY
    } = getBounds(clampedZoom);
    setZoom(clampedZoom);
    setPosition(currentPosition => ({
      x: clamp(currentPosition.x, -maxX, maxX),
      y: clamp(currentPosition.y, -maxY, maxY)
    }));
  };
  const updateZoom = (direction: 1 | -1) => {
    if (!isInteractionActive) setIsInteractionActive(true);
    setClampedZoom(zoom + direction * ZOOM_STEP);
  };
  const resetView = () => {
    setZoom(MIN_ZOOM);
    setPosition({
      x: 0,
      y: 0
    });
    setIsDragging(false);
    pointersRef.current.clear();
    dragStartRef.current = null;
    pinchStartRef.current = null;
  };
  const toggleInteraction = () => {
    if (isInteractionActive) {
      resetView();
      setIsInteractionActive(false);
      return;
    }
    setIsInteractionActive(true);
    setClampedZoom(1.25);
  };
  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isInteractionActive) return;
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY
    });
    const pointers = Array.from(pointersRef.current.values());
    if (pointers.length === 2) {
      pinchStartRef.current = {
        distance: getDistance(pointers[0], pointers[1]),
        zoom
      };
      dragStartRef.current = null;
    } else if (pointers.length === 1 && zoom > MIN_ZOOM) {
      dragStartRef.current = {
        pointerX: event.clientX,
        pointerY: event.clientY,
        x: position.x,
        y: position.y
      };
      setIsDragging(true);
    }
  };
  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!isInteractionActive || !pointersRef.current.has(event.pointerId)) {
      return;
    }
    event.preventDefault();
    pointersRef.current.set(event.pointerId, {
      x: event.clientX,
      y: event.clientY
    });
    const pointers = Array.from(pointersRef.current.values());
    if (pointers.length === 2 && pinchStartRef.current) {
      const distance = getDistance(pointers[0], pointers[1]);
      const ratio = distance / Math.max(pinchStartRef.current.distance, 1);
      setClampedZoom(pinchStartRef.current.zoom * ratio);
      return;
    }
    const dragStart = dragStartRef.current;
    if (pointers.length !== 1 || !dragStart || zoom === MIN_ZOOM) return;
    const {
      maxX,
      maxY
    } = getBounds();
    setPosition({
      x: clamp(dragStart.x + event.clientX - dragStart.pointerX, -maxX, maxX),
      y: clamp(dragStart.y + event.clientY - dragStart.pointerY, -maxY, maxY)
    });
  };
  const handlePointerEnd = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    pointersRef.current.delete(event.pointerId);
    pinchStartRef.current = null;
    dragStartRef.current = null;
    setIsDragging(false);
    const remainingPointer = Array.from(pointersRef.current.values())[0];
    if (remainingPointer && zoom > MIN_ZOOM) {
      dragStartRef.current = {
        pointerX: remainingPointer.x,
        pointerY: remainingPointer.y,
        x: position.x,
        y: position.y
      };
    }
  };
  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    if (!isInteractionActive) return;
    event.preventDefault();
    setClampedZoom(zoom + (event.deltaY < 0 ? ZOOM_STEP : -ZOOM_STEP));
  };
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isInteractionActive) return;
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      updateZoom(1);
      return;
    }
    if (event.key === '-') {
      event.preventDefault();
      updateZoom(-1);
      return;
    }
    const movementByKey: Record<string, Position> = {
      ArrowLeft: {
        x: 24,
        y: 0
      },
      ArrowRight: {
        x: -24,
        y: 0
      },
      ArrowUp: {
        x: 0,
        y: 24
      },
      ArrowDown: {
        x: 0,
        y: -24
      }
    };
    const movement = movementByKey[event.key];
    if (!movement || zoom === MIN_ZOOM) return;
    event.preventDefault();
    const {
      maxX,
      maxY
    } = getBounds();
    setPosition(currentPosition => ({
      x: clamp(currentPosition.x + movement.x, -maxX, maxX),
      y: clamp(currentPosition.y + movement.y, -maxY, maxY)
    }));
  };
  const toggleExpanded = () => {
    setIsExpanded(currentValue => {
      const nextValue = !currentValue;
      if (nextValue) {
        setIsInteractionActive(true);
        if (zoom === MIN_ZOOM) setClampedZoom(1.25);
      }
      return nextValue;
    });
  };
  const playerUrl = `https://www.youtube.com/embed/${VIDEO_ID}?controls=1&fs=0&playsinline=1&rel=0`;
  return <div className="max-w-4xl mx-auto mb-8" data-id="element-69" data-mp="2ysisrrbba1vz">
      <div className="mb-3 text-right" data-id="element-70" data-mp="2us77ccb4jxjg">
        <h3 className="text-2xl font-bold text-[#0A5F8F]" data-id="element-71" data-mp="2uvczk0f1vfje">
          מצלמת החוף בשידור חי
        </h3>
        <p className="text-gray-600 text-sm mt-1" data-id="element-72" data-mp="e6pis0wj1zr3">
          צפו בתנאים בחוף מעגן בזמן אמת
        </p>
      </div>

      <div ref={viewportRef} dir="ltr" tabIndex={0} onKeyDown={handleKeyDown} onWheel={handleWheel} onPointerDown={handlePointerDown} onPointerMove={handlePointerMove} onPointerUp={handlePointerEnd} onPointerCancel={handlePointerEnd} className={`overflow-hidden bg-black shadow-xl focus:outline-none focus:ring-2 focus:ring-[#0A5F8F] ${isExpanded ? 'fixed inset-0 z-[100] w-screen h-[100dvh] rounded-none border-0 focus:ring-inset' : 'relative aspect-video rounded-xl md:rounded-3xl border border-gray-200 focus:ring-offset-2'} ${isInteractionActive ? isDragging ? 'cursor-grabbing touch-none' : 'cursor-grab touch-none' : 'touch-pan-y'}`} aria-label={isInteractionActive ? 'מצלמת חוף במצב זום וגרירה. ניתן לגרור, לצבוט או להשתמש במקשי החיצים.' : 'מצלמת חוף בשידור חי'} data-id="element-73" data-mp="hmk3yg6xybrm">
        {isReady ? <iframe src={playerUrl} title="מצלמת חוף מעגן בשידור חי" className={`absolute inset-0 w-full h-full border-0 origin-center ${isInteractionActive ? 'pointer-events-none' : 'pointer-events-auto'} ${isDragging ? '' : 'transition-transform duration-200 ease-[cubic-bezier(0.23,1,0.32,1)] motion-reduce:transition-none'}`} style={{
        transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${zoom})`
      }} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerPolicy="strict-origin-when-cross-origin" data-id="element-74" data-mp="1o3o0fzuqw5h6" /> : <div className="w-full h-full flex items-center justify-center text-white text-sm" data-id="element-75" data-mp="3u2vradlbo6ag">
            טוען את מצלמת החוף…
          </div>}

        <div className="absolute top-3 inset-x-3 z-10 flex items-center justify-between gap-2" onPointerDown={event => event.stopPropagation()} data-id="element-76" data-mp="3p780xuv3t73d">
          <button type="button" onClick={event => {
          event.stopPropagation();
          toggleExpanded();
        }} className="h-11 px-3 rounded-xl bg-black/80 text-white hover:bg-black font-bold text-sm inline-flex items-center gap-2 transition-colors whitespace-nowrap" aria-label={isExpanded ? 'יציאה ממסך מלא' : 'הגדלה לכל המסך'} data-id="element-77" data-mp="2dodohn5b746b">
            {isExpanded ? <Minimize2Icon className="w-5 h-5" data-id="element-78" data-mp="2mcrft1j7oaw6" /> : <Maximize2Icon className="w-5 h-5" data-id="element-79" data-mp="2mem2aka4sb2d" />}
            {isExpanded ? 'יציאה' : 'כל המסך'}
          </button>

          <button type="button" onClick={event => {
          event.stopPropagation();
          toggleInteraction();
        }} className={`h-11 px-4 rounded-xl font-bold text-sm inline-flex items-center gap-2 transition-colors whitespace-nowrap ${isInteractionActive ? 'bg-amber-400 text-slate-950 hover:bg-amber-300' : 'bg-black/80 text-white hover:bg-black'}`} aria-pressed={isInteractionActive} data-id="element-80" data-mp="2w9w53a8csmh7">
            <MoveIcon className="w-5 h-5" data-id="element-81" data-mp="p8k6b68b17p6" />
            {isInteractionActive ? 'סיום הזזה' : 'הפעלת זום וגרירה'}
          </button>
        </div>

        {isInteractionActive && <div className="absolute bottom-3 left-1/2 z-10 -translate-x-1/2 flex items-center gap-1.5 rounded-xl bg-black/85 p-1.5 text-white shadow-lg" role="group" aria-label="פקדי זום למצלמת החוף" onPointerDown={event => event.stopPropagation()} data-id="element-82" data-mp="y5kp8xcludl8">
            <button type="button" onClick={() => updateZoom(-1)} disabled={zoom === MIN_ZOOM} className="w-10 h-10 rounded-lg hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center" aria-label="הקטנת התמונה" data-id="element-83" data-mp="1d2225t0g009w">
              <MinusIcon className="w-5 h-5" data-id="element-84" data-mp="1vvpdctzeezi0" />
            </button>
            <span className="min-w-14 text-center text-sm font-bold" aria-live="polite" data-id="element-85" data-mp="25yb8oa5iqqi9">
              {Math.round(zoom * 100)}%
            </span>
            <button type="button" onClick={() => updateZoom(1)} disabled={zoom === MAX_ZOOM} className="w-10 h-10 rounded-lg hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center" aria-label="הגדלת התמונה" data-id="element-86" data-mp="20xwyalgg34h2">
              <PlusIcon className="w-5 h-5" data-id="element-87" data-mp="2jq9by4cjts6i" />
            </button>
            <button type="button" onClick={resetView} disabled={zoom === MIN_ZOOM && position.x === 0 && position.y === 0} className="w-10 h-10 rounded-lg hover:bg-white/15 disabled:opacity-40 disabled:cursor-not-allowed transition-colors flex items-center justify-center" aria-label="איפוס התצוגה" data-id="element-88" data-mp="2js2h1f7ik4ce">
              <RotateCcwIcon className="w-4 h-4" data-id="element-89" data-mp="32nae7hepxe6r" />
            </button>
          </div>}
      </div>

      <p className="text-gray-500 text-xs text-center mt-2" data-id="element-90" data-mp="10s36widcs614">
        הפעילו את השידור בכפתור האדום של YouTube. לזום והזזה לחצו „הפעלת זום וגרירה”, ואז גררו, צבטו או השתמשו בגלגלת.
      </p>
    </div>;
}
function getDistance(first: Position, second: Position) {
  return Math.hypot(second.x - first.x, second.y - first.y);
}
function clamp(value: number, minimum: number, maximum: number) {
  return Math.min(Math.max(value, minimum), maximum);
}